import React, { useRef, useEffect, useState } from 'react';
import { getMapData } from './mapApi';
import { getBoundingBoxCenter2 } from './mapUtils';
import { baseScale, renderMap2, tileSize } from './mapRenderer';
import { Button, FormSelect } from 'react-bootstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { mapEnvTypes } from './mapEnvTypes';

const mapDataPromise = getMapData();

const Map = (props: Props) => {
  const initialRoomId = props.initialRoomId;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const eventCache = useRef([] as React.PointerEvent[]);
  const animationFrame = useRef<number | null>(null);
  const prevDiff = useRef(-1);
  const [mapData, setMapData] = useState<any | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState(1);
  const [selectedLevelId, setSelectedLevelId] = useState(0);
  const [selectedRoomId, setSelectedRoomId] = useState(178);
  const [roomSearch, setRoomSearch] = useState('');
  const [translate, setTranslate] = useState({ x: -21, y: 36 });
  const [scale, setScale] = useState<number>(10);
  const [mapDimensions, setMapDimensions] = useState({ width: 0, height: 0});

  useEffect(() => {
    const canvas = canvasRef.current!;
    contextRef.current = canvas.getContext('2d');
    canvas.focus();
    setMapDimensions({ width : canvas.offsetWidth, height: canvas.offsetHeight });

    const onResize = () => {
      setMapDimensions({ width : canvas.offsetWidth, height: canvas.offsetHeight });
    };

    mapDataPromise.then((data) => {
      setMapData(data);
      if (initialRoomId) {
        const initialRoom = data.roomsById[initialRoomId];
        if (initialRoom) {
          setSelectedAreaId(initialRoom.areaId);
          setSelectedLevelId(initialRoom.coordinates[2]);
          setSelectedRoomId(initialRoom.id);
          setTranslate({ x: initialRoom.coordinates[0], y: initialRoom.coordinates[1] });
        }
      }
    });

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [initialRoomId]);

  useEffect(() => {
    if (!mapData) {
      return;
    }

    if (animationFrame.current !== null) {
      cancelAnimationFrame(animationFrame.current);
    }
    animationFrame.current = requestAnimationFrame(() => {
      animationFrame.current = null;
      const canvas = canvasRef.current!;
      const context = contextRef.current!;
      context.clearRect(0, 0, context.canvas.width, context.canvas.height);
      context.save();
      context.translate(canvas.width / 2 - translate.x * scale, canvas.height / 2 - translate.y * scale);
      context.scale(scale / baseScale, scale / baseScale);
      renderMap2(canvas, context, mapData, selectedAreaId, selectedLevelId, selectedRoomId);
      context.restore();
    });

  }, [mapData, selectedAreaId, selectedLevelId, selectedRoomId, translate, scale, mapDimensions]);

  const handleSelectedAreaIdChange = (areaId: number) => {
    const area = mapData.areasById[areaId];
    if (!area) {
      return;
    }
    setSelectedAreaId(area.id);

    const level = area.levelsById[0] ?? area.levels[0];
    if (!level) {
      return;
    }
    setSelectedLevelId(level.id);

    const selectedRoom = mapData.roomsById[selectedRoomId];
    if (selectedRoom && selectedRoom?.areaId === area.id && selectedRoom?.coordinates[2] === level.id) {
      setTranslate({ x: selectedRoom.coordinates[0], y: selectedRoom.coordinates[1] });
      return;
    }

    const rooms = level.rooms;
    if (!rooms.length) {
      return;
    }

    const closest = getBoundingBoxCenter2(rooms);
    setTranslate({ x: closest.coordinates[0], y: closest.coordinates[1] });
  }

  const handleSelectedLevelIdChange = (levelId: number) => {
    const area = mapData.areasById[selectedAreaId];
    if (!area) {
      return;
    }

    const level = area.levelsById[levelId];
    if (!level) {
      return;
    }
    setSelectedLevelId(level.id);

    const selectedRoom = mapData.roomsById[selectedRoomId];
    if (selectedRoom && selectedRoom?.areaId === area.id && selectedRoom?.coordinates[2] === level.id) {
      setTranslate({ x: selectedRoom.coordinates[0], y: selectedRoom.coordinates[1] });
      return;
    }

    const rooms = level.rooms;
    if (!rooms.length) {
      return;
    }

    const closest = getBoundingBoxCenter2(rooms);
    setTranslate({ x: closest.coordinates[0], y: closest.coordinates[1] });
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    canvasRef.current?.setPointerCapture(e.pointerId);
    eventCache.current.push(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const pointerCount = eventCache.current.length;
    const eventIndex = eventCache.current.findIndex((cached) => cached.pointerId === e.pointerId);
    const lastEvent = eventCache.current[eventIndex];
    eventCache.current[eventIndex] = e;

    //single pointer (pan)
    if (pointerCount === 1) {
      const deltaX = e.clientX - lastEvent.clientX;
      const deltaY = e.clientY - lastEvent.clientY;
      if (Math.abs(deltaX) >= 1 || Math.abs(deltaY) >= 1){
        setTranslate({
          x: translate.x - deltaX / scale,
          y: translate.y - deltaY / scale,
        });
      }
    }

    //double pointer (pinch zoom)
    if (pointerCount === 2) {
      const curDiff = Math.abs(eventCache.current[0].clientX - eventCache.current[1].clientX);
      if (prevDiff.current > 0) {
        if (curDiff > prevDiff.current) {
          updateScale(scale * 1.05);
        } else if (curDiff < prevDiff.current) {
          updateScale(scale * .95);
        }
      }
      prevDiff.current = curDiff;
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    canvasRef.current?.releasePointerCapture(e.pointerId);
    const pointerCount = eventCache.current.length;
    const eventIndex = eventCache.current.findIndex((cached) => cached.pointerId === e.pointerId);
    eventCache.current.splice(eventIndex, 1);

    //double pointer (pinch zoom)
    if (pointerCount === 2) {
      prevDiff.current = -1;
    }
  };

  const handleCellClick = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!mapData) {
      return;
    }

    const canvas = canvasRef.current!;
    const coords = { x: e.clientX - canvas.getBoundingClientRect().left, y: e.clientY - canvas.getBoundingClientRect().top };
    const mapX = (coords.x - canvas.width / 2 + translate.x * scale) / scale;
    const mapY = (coords.y - canvas.height / 2 + translate.y * scale) / scale;

    // Calculate the clicked cell indices
    const rooms = mapData.areasById[selectedAreaId]?.levelsById[selectedLevelId]?.rooms ?? [];
    for (const room of rooms) {
      const [x, y] = room.coordinates;
      if (mapX >= x - tileSize && mapX < x + tileSize &&
        mapY >= y - tileSize && mapY < y + tileSize) {
        setSelectedRoomId(room.id);
        break;
      }
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    updateScale(scale * (1 + e.deltaY / 1000));
  };
  
  const handleZoomIn = () => {
    updateScale(scale * 1.2);
  };

  const handleZoomOut = () => {
    updateScale(scale * 0.8);
  };

  const updateScale = (newScale: number) => {
    setScale(Math.max(Math.min(newScale, 50), .5));
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLCanvasElement>)=> {
    e.preventDefault();
    switch (e.key.toLowerCase()) {
      case 'n':
      case 'arrowup':
        moveDirection('north');
        break;
      case 's':
      case 'arrowdown':
        moveDirection('south');
        break;
      case 'e':
      case 'arrowright':
        moveDirection('east');
        break;
      case 'w':
      case 'arrowleft':
        moveDirection('west');
        break;
      case 'u':
        moveDirection('up');
        break;
      case 'd':
        moveDirection('down');
        break;
      }
  }

  function moveDirection(name: string) {
    const selectedRoom = mapData?.roomsById[selectedRoomId];
    if (!selectedRoom) {
      return;
    }
    
    const exit = selectedRoom.exits.find((x: any) => x.name === name);
    if (exit) {
      const exitRoom = mapData?.roomsById[exit.exitId];
      if (exitRoom) {
        setSelectedRoomId(exitRoom.id);
        setTranslate({ x: exitRoom.coordinates[0], y: exitRoom.coordinates[1] });
        setSelectedAreaId(exitRoom.areaId);
        setSelectedLevelId(exitRoom.coordinates[2]);
      }
    }
  }

  const filterOption = (candidate: any, input: string): any => {
    if (input.length < 4) {
      return undefined;
    }
    return candidate.label.toLowerCase().includes(input.toLowerCase());
  };

  const noOptionsMessage = (obj: { inputValue: string}) => {
    if (obj.inputValue.length < 4) {
      return 'Search input must be at least 4 characters';
    }
    return 'No search results';
  }

  const areaOptions = mapData?.areaOptions ?? [];
  const levelOptions = mapData?.areasById[selectedAreaId]?.levelOptions ?? [];
  const roomOptions = mapData?.roomOptions ?? [];
  const selectedRoom = mapData?.roomsById[selectedRoomId];

  function handleRoomSearchChange(item: any): void {
    const room = mapData?.roomsById[item.value];
    if (room) {
      setSelectedRoomId(room.id);
      setTranslate({ x: room.coordinates[0], y: room.coordinates[1] });
      setSelectedAreaId(room.areaId);
      setSelectedLevelId(room.coordinates[2]);
      setRoomSearch('');
    }
  }

  return (
    <div>
        <div className="d-flex flex-wrap">
          <div className="me-2 mb-2">
            <label className="xsmall mb-0">AREA</label>
            <FormSelect size="sm" className="form-control w-auto" value={selectedAreaId} onChange={(e) => handleSelectedAreaIdChange(parseInt(e.target.value))}>
              {areaOptions.map((x: any) => <option key={x.value} value={x.value}>{x.text}</option>)}
            </FormSelect>
          </div>

          <div className="me-2 mb-2">
            <label className="xsmall mb-0">FLOOR</label>          
            <FormSelect size="sm" value={selectedLevelId} onChange={(e) => handleSelectedLevelIdChange(parseInt(e.target.value))} style={{ width: '60px' }}>
              {levelOptions.map((x: any) => <option key={x.value} value={x.value}>{x.text}</option>)}
            </FormSelect>
          </div>

          <div className="me-2 mb-2">
            <label className="xsmall mb-0">ZOOM</label>
            <div className="d-flex">
              <Button className="me-1" size="sm" onClick={handleZoomIn} style={{width: '40px'}}><FontAwesomeIcon icon={faPlus} /></Button>
              <Button size="sm" onClick={handleZoomOut} style={{width: '40px'}}><FontAwesomeIcon icon={faMinus} /></Button>
            </div>
          </div>

          <div className="mb-2" style={{flex: "0 1 300px"}}>
            <label className="xsmall mb-0">GO TO ROOM</label>
            <div className="text-muted">
              <Select
                value={roomSearch}
                onChange={e => handleRoomSearchChange(e)}
                unstyled
                className="map-rooms-autocomplete"
                classNamePrefix="autocomplete"
                maxMenuHeight={300}
                options={roomOptions}
                filterOption={filterOption}
                noOptionsMessage={noOptionsMessage}
                placeholder="Search..."
              />
            </div>
          </div>
        </div>

        <div className="map-row row gutter-y-0">
          <div className="map-col1 col-lg-8 mh-100 mb-3">
            <canvas
              ref={canvasRef}
              width={mapDimensions.width}
              height={mapDimensions.height}
              className="map-canvas w-100 h-100"
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerMove={handlePointerMove}
              onMouseDown={handleCellClick}
              onWheel={handleWheel}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              style={{userSelect:'none', touchAction: 'none'}}
            />
          </div>
          <div className="col-lg-4 mh-100 overflow-auto mb-3">
            {selectedRoom ? <>
                <p>
                  <div>
                    <span className="fw-600" style={{color: '#008B8B'}}>{selectedRoom.name} / </span>
                    <span style={{color: '#008B8B'}}>{selectedRoom.id}</span>
                  </div>
                  <div>
                    <span style={{color: '#006B6B'}}>{selectedRoom.userData?.zone} - </span> 
                    <span style={{color: '#006B6B'}}>{mapEnvTypes[selectedRoom.environment].name}</span>
                  </div>
                </p>
                <p>
                  {selectedRoom.userData.description}
                </p>
                <p>
                  <span className="fw-600 text-muted">Exits: </span>
                  <span className="text-muted">[{selectedRoom.exits.map((x: any) => x.name).join(', ')}]</span> 
                </p>

                {selectedRoom.id === 182 ? <p className="mt-4"><span className="fw-600"><span style={{color: '#fff'}}>*</span><span style={{color: '#aa0000'}}>Bryan</span><span style={{color: '#fff'}}>*</span></span> is here, riding a shadow stallion.</p> : null}


            </> : null}
          </div>
        </div>

        <div className="text-muted small">
          You can use your keyboard to traverse the map: Arrow keys and/or N, S, E, W, U, D<br/>
          This online map was inspired by and built upon <a href="https://github.com/weisluke/WoTMUD" className="text-muted">Luke's WotMud map</a> for Mudlet.
        </div>

    </div>
  );
};

interface Props {
  initialRoomId?: number;
}

export default Map;